import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Link,
  Divider,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  PolicyOutlined,
  CheckCircleOutline,
  CancelOutlined,
  SecurityOutlined,
  EmailOutlined,
  TimerOutlined,
  GavelOutlined,
  ArrowBack,
} from "@mui/icons-material";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode } from "../utils";
import { useNavigate } from "react-router-dom";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface VulnerabilityDisclosurePolicyProps {
  mode: string;
}

const VulnerabilityDisclosurePolicy: React.FC<
  VulnerabilityDisclosurePolicyProps
> = ({ mode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Get logo URL with dark mode support
  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";
  const domain = whitelabelSettings?.webDomain || window.location.hostname;
  // Use whitelabel security email, owner email, sales email, or construct from domain
  const securityEmail = whitelabelSettings?.securityEmail || whitelabelSettings?.ownerEmail || whitelabelSettings?.salesEmail || "security@" + window.location.hostname;

  const inScopeItems = [
    `${domain} and its subdomains`,
    `${appName} web applications`,
    `${appName} APIs`,
    `${appName} mobile applications`,
  ];

  const outOfScopeItems = [
    "Third-party services and applications",
    "Social engineering attacks",
    "Physical security issues",
    "Denial of Service (DoS/DDoS) attacks",
    "Spam or phishing attempts",
    "Vulnerabilities in outdated browsers",
    "Issues already known or being addressed",
  ];

  const doItems = [
    `Report vulnerabilities promptly via ${securityEmail}`,
    "Provide sufficient detail to reproduce the vulnerability",
    "Allow us reasonable time (90 days) to address the issue before public disclosure",
    "Act in good faith to avoid privacy violations, data destruction, or service disruption",
    "Only interact with accounts you own or have explicit permission to test",
    "Stop testing if you access sensitive data and report immediately",
  ];

  const dontItems = [
    "Access, modify, or delete data belonging to other users",
    "Perform actions that could harm the availability of our services",
    "Use automated scanning tools without prior approval",
    "Publicly disclose vulnerabilities before we've had time to address them",
    "Demand payment or compensation as a condition for reporting",
    "Engage in any illegal activity",
  ];

  const commitments = [
    {
      icon: <TimerOutlined />,
      title: "48-hour Acknowledgment",
      description: "We will acknowledge receipt of your report within 48 hours",
    },
    {
      icon: <EmailOutlined />,
      title: "Open Communication",
      description: "We will keep you informed about the status of your report",
    },
    {
      icon: <SecurityOutlined />,
      title: "Timely Remediation",
      description: "We will work to remediate valid vulnerabilities promptly",
    },
    {
      icon: <GavelOutlined />,
      title: "No Legal Action",
      description:
        "We will not pursue legal action against researchers acting in good faith",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Vulnerability Disclosure Policy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Guidelines for responsible security research
          </Typography>
        </Container>
      </Box>

      {/* Back Link */}
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Link
          component="button"
          onClick={() => navigate("/security")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "text.secondary",
            textDecoration: "none",
            "&:hover": { color: instanceConfig.primaryColor },
          }}
        >
          <ArrowBack fontSize="small" />
          Back to Security
        </Link>
      </Container>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          <strong>Last Updated:</strong> December 2024
        </Typography>

        <Typography variant="body1" paragraph>
          {appName} is committed to ensuring the security of our customers and
          their data. We value the contributions of security researchers and
          welcome responsible disclosure of vulnerabilities.
        </Typography>

        {/* Scope Section */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Scope
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: instanceConfig.primaryColor }}
                >
                  In Scope
                </Typography>
                <List dense>
                  {inScopeItems.map((item, idx) => (
                    <ListItem key={idx} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutline
                          sx={{ color: instanceConfig.primaryColor }}
                          fontSize="small"
                        />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="text.secondary"
                >
                  Out of Scope
                </Typography>
                <List dense>
                  {outOfScopeItems.map((item, idx) => (
                    <ListItem key={idx} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CancelOutlined color="disabled" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Guidelines Section */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Guidelines for Researchers
        </Typography>

        <Typography variant="body1" paragraph>
          When conducting security research, we ask that you:
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderLeft: 4,
                borderColor: instanceConfig.primaryColor,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: instanceConfig.primaryColor }}
                >
                  Do
                </Typography>
                <List dense>
                  {doItems.map((item, idx) => (
                    <ListItem key={idx} disableGutters sx={{ alignItems: "flex-start" }}>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        <CheckCircleOutline
                          sx={{ color: instanceConfig.primaryColor }}
                          fontSize="small"
                        />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{ height: "100%", borderLeft: 4, borderColor: "divider" }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="text.secondary"
                >
                  Do Not
                </Typography>
                <List dense>
                  {dontItems.map((item, idx) => (
                    <ListItem key={idx} disableGutters sx={{ alignItems: "flex-start" }}>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        <CancelOutlined color="disabled" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Our Commitment Section */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Our Commitment
        </Typography>

        <Typography variant="body1" paragraph>
          When you report a vulnerability in accordance with this policy, we
          commit to:
        </Typography>

        <Grid container spacing={3}>
          {commitments.map((commitment, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card sx={{ height: "100%", textAlign: "center" }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: alpha(instanceConfig.primaryColor, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      color: instanceConfig.primaryColor,
                    }}
                  >
                    {commitment.icon}
                  </Box>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {commitment.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {commitment.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Safe Harbor Section */}
        <Card sx={{ mt: 6, bgcolor: alpha(instanceConfig.primaryColor, 0.05) }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Safe Harbor
            </Typography>
            <Typography variant="body1" paragraph>
              We consider security research conducted in accordance with this
              policy to be:
            </Typography>
            <List>
              <ListItem disableGutters>
                <ListItemIcon>
                  <CheckCircleOutline color="primary" />
                </ListItemIcon>
                <ListItemText primary="Authorized concerning any applicable anti-hacking laws" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon>
                  <CheckCircleOutline color="primary" />
                </ListItemIcon>
                <ListItemText primary="Authorized concerning any relevant anti-circumvention laws" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon>
                  <CheckCircleOutline color="primary" />
                </ListItemIcon>
                <ListItemText primary="Exempt from restrictions in our Terms of Service that would interfere with security research" />
              </ListItem>
            </List>
            <Typography variant="body1" sx={{ mt: 2 }}>
              We will not pursue civil or criminal action, or send notice to law
              enforcement, for security research conducted in good faith
              according to this policy.
            </Typography>
          </CardContent>
        </Card>

        {/* Remediation Timeline */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Remediation Timeline
        </Typography>

        <Card>
          <CardContent>
            <Typography variant="body1" paragraph>
              We aim to address vulnerabilities according to the following SLAs:
            </Typography>
            <Grid container spacing={2}>
              {[
                { severity: "Critical", time: "24 hours", color: "#d32f2f" },
                { severity: "High", time: "7 days", color: "#ed6c02" },
                { severity: "Medium", time: "30 days", color: "#0288d1" },
                { severity: "Low", time: "90 days", color: "#2e7d32" },
              ].map((item, idx) => (
                <Grid item xs={6} sm={3} key={idx}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      borderRadius: 1,
                      bgcolor: alpha(item.color, 0.1),
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: item.color, fontWeight: "bold" }}
                    >
                      {item.severity}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.time}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card sx={{ mt: 6 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              For security-related inquiries:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailOutlined color="action" />
                <Link href={`mailto:${securityEmail}`}>{securityEmail}</Link>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SecurityOutlined color="action" />
                <Link href="/.well-known/pgp-key.txt">
                  PGP Key for encrypted communications
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Recognition */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography variant="h6" gutterBottom>
            Get Recognized
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Valid vulnerability reports may be recognized in our Security Hall
            of Fame.
          </Typography>
          <Link
            href="/security/hall-of-fame"
            sx={{
              color: instanceConfig.primaryColor,
              textDecoration: "none",
              fontWeight: "medium",
            }}
          >
            View Hall of Fame →
          </Link>
        </Box>
      </Container>

      <BrandedPageFooter currentPage="security" />
    </Box>
  );
};

export default VulnerabilityDisclosurePolicy;
